import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCar /*, faPeopleLine*/,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import {
  Icon,
  UilCube /* , UilFilesLandscapesAlt */
} from '@iconscout/react-unicons';
import { useAppContext } from 'providers/AppProvider';
import { useMemo } from 'react';

interface UserAccess {
  id_modulo: number;
  id_permissao: number;
}

export interface Route {
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: 'font-awesome' | 'feather' | 'unicons';
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  isNext?: boolean;
}

export interface RouteItems {
  label: string;
  horizontalNavLabel?: string;
  icon: Icon;
  labelDisabled?: boolean;
  pages: Route[];
  megaMenu?: boolean;
  active?: boolean;
}

// Core permission checking logic (pure function)
export const checkModuleAccess = (
  userModules: UserAccess[] | undefined,
  accessTitle: number,
  requiredLevel: number = 1
): boolean => {
  if (!userModules || !Array.isArray(userModules)) return false;

  const accessEntry = userModules.find(
    module => module.id_modulo === accessTitle
  );

  return accessEntry ? accessEntry.id_permissao >= requiredLevel : false;
};
// React hook for permissions using AppContext
export const usePermissions = () => {
  const { getUserModules } = useAppContext();
  const userModules = getUserModules();

  const hasAccess = useMemo(
    () =>
      (accessTitle: number, requiredLevel: number = 1) => {
        return checkModuleAccess(userModules, accessTitle, requiredLevel);
      },
    [userModules]
  );

  return {
    hasAccess,
    userModules
  };
};

// Hook for reactive permission checking (replacement for userHasAccess)
export const useModuleAccess = (
  accessTitle: number,
  requiredLevel: number = 1
) => {
  const { hasAccess } = usePermissions();

  // Simple memoization - recalculates only when dependencies change
  return useMemo(
    () => hasAccess(accessTitle, requiredLevel),
    [hasAccess, accessTitle, requiredLevel]
  );
};

// Generate routes based on current permissions
export const useRoutes = (): RouteItems[] => {
  const { hasAccess } = usePermissions();

  return useMemo((): RouteItems[] => {
    const generatedRoutes: RouteItems[] = [];

    // Car Management section with dynamic structure
    if (hasAccess(1, 1)) {
      const carRoute: Route = {
        name: 'Gestão de carros',
        iconSet: 'font-awesome' as const,
        icon: faCar,
        active: true
      };

      // If user has level 2+ access, create cascading menu
      if (hasAccess(1, 1)) {
        carRoute.name = 'Gestão de Carros';
        carRoute.pages = [
          {
            name: '1. Agendar pedido',
            path: '/dashboard/carManagement/pendingTable',
            pathName: 'pendingTable',
            active: true
          },
          {
            name: '2. Plano semanal',
            path: '/dashboard/carManagement/finalizedTable',
            pathName: 'finalizedTable',
            active: true
          },
          {
            name: '3. Saídas',
            path: '/viagens/procurar',
            pathName: 'travels',
            active: true
          },
          // {
          //   name: 'Viagem',
          //   path: 'carManagement/tripmanagement/0',
          //   pathName: 'tripmanagement',
          //   active: true
          // },
          // Add more sub-pages based on higher permission levels
          ...(hasAccess(1, 2)
            ? [
                {
                  name: 'Admin',
                  path: 'admin',
                  active: true,
                  pages: [
                    {
                      name: 'Alocação de carros',
                      path: '/dashboard/carManagement/alocationTable',
                      pathName: 'alocationTable',
                      active: true
                    },
                    {
                      name: 'Gestão dos carros',
                      path: '/dashboard/carManagement/carStateManagement',
                      pathName: 'carStateManagement',
                      active: true
                    }
                  ]
                }
              ]
            : [])
        ];
      }

      generatedRoutes.push({
        label: 'carManagement',
        icon: UilCube,
        labelDisabled: true,
        pages: [carRoute]
      });
    }

    if (hasAccess(6, 4)) {
      const financeRoute: Route = {
        name: 'Gestão de finanças',
        iconSet: 'font-awesome' as const,
        icon: faFileContract, // faDatabase or faFileContract
        active: true
      };

      financeRoute.name = 'Estatísticas';
      financeRoute.pages = [
        {
          name: 'Vendas',
          path: '/dashboard/FinanceManagement/powerBiTableVendas',
          pathName: 'powerBiTable',
          active: true
        },
        {
          name: 'Laboratório',
          path: '/dashboard/FinanceManagement/powerBiTableLaboratorio',
          pathName: 'powerBiTableLaboratorio',
          active: true
        }
      ];

      generatedRoutes.push({
        label: 'financeManagement',
        icon: UilCube,
        labelDisabled: true,
        pages: [financeRoute]
      });
    }

    // room Management section with dynamic structure
    // if (hasAccess(2, 1)) {
    //   const roomRoute: Route = {
    //     name: 'Gestão de salas',
    //     iconSet: 'font-awesome' as const,
    //     icon: faPeopleLine,
    //     active: true
    //   };

    //   // If user has level 2+ access, create cascading menu
    //   if (hasAccess(2, 2)) {
    //     roomRoute.name = 'Gestão de Salas';
    //     roomRoute.pages = [
    //       {
    //         name: 'Calendario',
    //         path: 'appsendar',
    //         pathName: 'app-lendar',
    //         active: true
    //       },
    //       // Add more sub-pages based on higher permission levels
    //       ...(hasAccess(2, 3)
    //         ? [
    //             {
    //               name: 'Relórios',
    //               path: 'apps/rorts',
    //               pathName: 'appports',
    //               active: true
    //             }
    //           ]
    //         : []),
    //       ...(hasAccess(2, 4)
    //         ? [
    //             {
    //               name: 'Configues',
    //               path: 'apps/seings',
    //               pathName: 'app-seings',
    //               active: true
    //             }
    //           ]
    //         : [])
    //     ];
    //   } else {
    //     // Level 1 access: direct link to main functionality
    //     roomRoute.name = 'Calendario das salas';
    //     roomRoute.path = 'applendar';
    //     roomRoute.pathName = 'applendar';
    //   }

    //   generatedRoutes.push({
    //     label: 'apps',
    //     icon: UilCube,
    //     labelDisabled: true,
    //     pages: [roomRoute]
    //   });
    // }
    // // Settings section
    // if (hasAccess(3, 2)) {
    //   generatedRoutes.push({
    //     label: 'Settings',
    //     icon: UilFilesLandscapesAlt,
    //     pages: [
    //       {
    //         name: 'errors',
    //         icon: 'alert-triangle',
    //         active: true,
    //         pages: [
    //           {
    //             name: '403',
    //             path: 'pages/errors/403',
    //             pathName: 'error403',
    //             active: true
    //           },
    //           {
    //             name: '404',
    //             path: 'pages/errors/404',
    //             pathName: 'error404',
    //             active: true
    //           },
    //           {
    //             name: '500',
    //             path: 'pages/errors/500',
    //             pathName: 'error500',
    //             active: true
    //           }
    //         ]
    //       },
    //       // Authentication pages
    //       {
    //         name: 'authentication',
    //         icon: 'lock',
    //         active: true,
    //         pages: [
    //           {
    //             name: 'simple',
    //             active: true,
    //             pages: [
    //               {
    //                 name: 'sign-in',
    //                 path: '/pages/authentication/simple/sign-in',
    //                 pathName: 'simple-signin',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-up',
    //                 path: '/pages/authentication/simple/sign-up',
    //                 pathName: 'simple-signup',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-out',
    //                 path: '/pages/authentication/simple/sign-out',
    //                 pathName: 'simple-signout',
    //                 active: true
    //               },
    //               {
    //                 name: 'forgot-password',
    //                 path: '/pages/authentication/simple/forgot-password',
    //                 pathName: 'simple-forgot-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'reset-password',
    //                 path: '/pages/authentication/simple/reset-password',
    //                 pathName: 'simple-reset-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'lock-screen',
    //                 path: '/pages/authentication/simple/lock-screen',
    //                 pathName: 'simple-lock-screen',
    //                 active: true
    //               },
    //               {
    //                 name: '2FA',
    //                 path: '/pages/authentication/simple/2FA',
    //                 pathName: 'simple-2FA',
    //                 active: true
    //               }
    //             ]
    //           },
    //           {
    //             name: 'split',
    //             active: true,
    //             pages: [
    //               {
    //                 name: 'sign-in',
    //                 path: '/pages/authentication/split/sign-in',
    //                 pathName: 'split-signin',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-up',
    //                 path: '/pages/authentication/split/sign-up',
    //                 pathName: 'split-signup',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-out',
    //                 path: '/pages/authentication/split/sign-out',
    //                 pathName: 'split-signout',
    //                 active: true
    //               },
    //               {
    //                 name: 'forgot-password',
    //                 path: '/pages/authentication/split/forgot-password',
    //                 pathName: 'split-forgot-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'reset-password',
    //                 path: '/pages/authentication/split/reset-password',
    //                 pathName: 'split-reset-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'lock-screen',
    //                 path: '/pages/authentication/split/lock-screen',
    //                 pathName: 'split-lock-screen',
    //                 active: true
    //               },
    //               {
    //                 name: '2FA',
    //                 path: '/pages/authentication/split/2FA',
    //                 pathName: 'split-2FA',
    //                 active: true
    //               }
    //             ]
    //           },
    //           {
    //             name: 'Card',
    //             active: true,
    //             pages: [
    //               {
    //                 name: 'sign-in',
    //                 path: 'pages/authentication/card/sign-in',
    //                 pathName: 'card-signin',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-up',
    //                 path: 'pages/authentication/card/sign-up',
    //                 pathName: 'card-signup',
    //                 active: true
    //               },
    //               {
    //                 name: 'sign-out',
    //                 path: 'pages/authentication/card/sign-out',
    //                 pathName: 'card-signout',
    //                 active: true
    //               },
    //               {
    //                 name: 'forgot-password',
    //                 path: 'pages/authentication/card/forgot-password',
    //                 pathName: 'card-forgot-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'reset-password',
    //                 path: 'pages/authentication/card/reset-password',
    //                 pathName: 'card-reset-password',
    //                 active: true
    //               },
    //               {
    //                 name: 'lock-screen',
    //                 path: 'pages/authentication/card/lock-screen',
    //                 pathName: 'card-lock-screen',
    //                 active: true
    //               },
    //               {
    //                 name: '2FA',
    //                 path: '/pages/authentication/card/2FA',
    //                 pathName: 'card-2FA',
    //                 active: true
    //               }
    //             ]
    //           }
    //         ]
    //       }
    //     ]
    //   });
    // }

    return generatedRoutes;
  }, [hasAccess]);
};
